import { CommonModule } from '@angular/common';
import { Component, Directive, Inject, Input, NgModule, Optional, OnChanges, SimpleChanges } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MAT_SNACK_BAR_DATA, MatSnackBarRef } from '@angular/material/snack-bar';
import { NotificationMessageLogService } from '@services/notification-message-log.service';
import { finalize } from 'rxjs/operators';

export interface WSNoticeData {
  title?: string;
  message?: string;
  messages?: string[];
  variant?: 'info' | 'success' | 'warning' | 'error' | 'evento';
  closeLabel?: string;
  ctaUrl?: string;
  icon?: string;
  dismissible?: boolean;
  displayType?: 'toast' | 'banner';
  /** When set, CTA / dismiss interactions are logged for admin message banners. */
  notificationId?: string;
}

@Directive()
abstract class BaseWSNoticeComponent implements OnChanges {
  isClosed = false;

  @Input() previewData?: WSNoticeData;

  constructor(
    @Optional() public snackBarRef: MatSnackBarRef<any> | null,
    @Optional() @Inject(MAT_SNACK_BAR_DATA) public injectedData: WSNoticeData | null,
    @Optional() protected messageLogService: NotificationMessageLogService | null
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    // When the preview content changes, re-show the component.
    // This is mostly important for ws-banner preview (no MatSnackBarRef).
    if (changes['previewData']) {
      const next = changes['previewData'].currentValue as WSNoticeData | undefined;
      if (next) {
        this.isClosed = false;
      }
    }
  }

  get data(): WSNoticeData {
    if (this.isClosed) {
      return {};
    }

    return this.previewData || this.injectedData || {};
  }

  getWrapperClasses(): string[] {
    return [
      this.data.variant || 'info',
      this.data.displayType === 'banner' ? 'banner-mode' : 'toast-mode'
    ];
  }

  getIcon(): string {
    if (this.data.icon) {
      return this.data.icon;
    }

    switch (this.data.variant) {
      case 'warning':
        return 'warning_amber';
      case 'evento':
        return 'calendar_today';
      case 'error':
        return 'error_outline';
      case 'success':
        return 'check_circle_outline';
      case 'info':
      default:
        return 'info_outline';
    }
  }

  onCloseClick(): void {
    const id = this.data.notificationId;
    if (!id || !this.messageLogService) {
      this.close();
      return;
    }
    this.messageLogService
      .dismissBanner(id)
      .pipe(finalize(() => this.close()))
      .subscribe({ error: () => {} });
  }

  onCtaClick(event: MouseEvent, url: string | undefined): void {
    if (!url) {
      return;
    }
    const id = this.data.notificationId;
    if (!id || !this.messageLogService) {
      return;
    }
    event.preventDefault();
    this.messageLogService
      .log(id, 'CTA_CLICK')
      .pipe(finalize(() => window.open(url, '_blank', 'noopener,noreferrer')))
      .subscribe({ error: () => window.open(url, '_blank', 'noopener,noreferrer') });
  }

  close() {
    this.isClosed = true;
    this.snackBarRef?.dismiss();
  }
}

@Component({
  selector: 'ws-toast',
  host: {
    class: 'custom-toast-host'
  },
  templateUrl: './wstoast.component.html',
  styleUrls: ['./wstoast.component.scss']
})
export class WSToastComponent extends BaseWSNoticeComponent {
  constructor(
    @Optional() snackBarRef: MatSnackBarRef<WSToastComponent> | null,
    @Optional() @Inject(MAT_SNACK_BAR_DATA) injectedData: WSNoticeData | null,
    @Optional() messageLogService: NotificationMessageLogService | null
  ) {
    super(snackBarRef, injectedData, messageLogService);
  }
}

@Component({
  selector: 'ws-banner',
  host: {
    class: 'custom-toast-host'
  },
  templateUrl: './wstoast.component.html',
  styleUrls: ['./wstoast.component.scss']
})
export class WsBannerComponent extends BaseWSNoticeComponent {
  @Input() previewData?: WSNoticeData;

  constructor(
    @Optional() snackBarRef: MatSnackBarRef<WsBannerComponent> | null,
    @Optional() @Inject(MAT_SNACK_BAR_DATA) injectedData: WSNoticeData | null,
    @Optional() messageLogService: NotificationMessageLogService | null
  ) {
    super(snackBarRef, injectedData, messageLogService);
  }
}

@NgModule({
  declarations: [WSToastComponent, WsBannerComponent],
  imports: [CommonModule, MatIconModule],
  exports: [WSToastComponent, WsBannerComponent]
})
export class WsBannerModule {}
